/**
 * ServicesSection.jsx
 * Dynamic Services with DB Image Path Support
 */

import { useState, useEffect } from "react";
import { apiFetch } from "../../helpers";

const COLORS = [
  "#1D9E75",
  "#E8323C",
  "#F59E0B",
  "#8B5CF6",
  "#06B6D4",
  "#EC4899",
  "#10B981",
  "#F97316",
];

export default function ServicesSection({ onBookAppointment }) {
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const json = await apiFetch(`${process.env.REACT_APP_GET_ALL_SERVICES}`);

      if (json.status) {
        const rows = (json.data || []).map((item, index) => ({
          id: item.service_id,
          title: item.service_name,
          desc: item.description,
          price: item.price,
          image: item.image,
          color: COLORS[index % COLORS.length],
        }));

        setServices(rows);
      }
    } catch (err) {
      console.log(err);
    }
  };

  /* DB path example:
     omdentalclinic\uploads\service\general.jpg
  */
  const imageUrl = (img) => {
    if (!img) return "";

    const cleanPath = img.replace(/\\/g, "/");
    const baseUrl = process.env.REACT_APP_WEB_URL;
    return `${baseUrl}/${cleanPath.replace(/^\/+/, "")}`;
  };

  return (
    <section id="services" style={{ ...S.section, minHeight: "calc(100vh - 90px)" }}>
      <div
        style={{
          padding: 33,
        }}
      ></div>
      <div style={S.container}>
        <div style={{ marginBottom: 40 }}>
          <h2 style={S.heading}>
            Our <span style={{ color: "#1D9E75" }}>Services</span>
          </h2>

          <p style={S.sub}>World-class dental treatments under one roof</p>
        </div>

        <div style={S.grid}>
          {services.map((svc) => (
            <ServiceCard
              key={svc.id}
              svc={svc}
              imageUrl={imageUrl}
              onView={() => setSelected(svc)}
              onBook={() => onBookAppointment && onBookAppointment()}
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      {selected && (
        <div style={S.overlay} onClick={() => setSelected(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <button style={S.closeBtn} onClick={() => setSelected(null)}>
              ✕
            </button>

            <div
              style={{
                textAlign: "center",
                marginBottom: 20,
              }}
            >
              <img
                src={imageUrl(selected.image)}
                alt={selected.title}
                style={{
                  width: 90,
                  height: 90,
                  objectFit: "cover",
                  borderRadius: 16,
                  marginBottom: 14,
                }}
                onError={(e) => (e.target.style.display = "none")}
              />

              <h2 style={{ color: "#fff" }}>{selected.title}</h2>
            </div>

            <p style={S.modalDesc}>{selected.desc}</p>

            <div style={S.priceBox}>💰 {selected.price}</div>

            {/* <button
              style={S.bookBtn}
              onClick={() => {
                setSelected(null);

                onBookAppointment && onBookAppointment();
              }}
            >
              📅 Book Appointment
            </button> */}
          </div>
        </div>
      )}
    </section>
  );
}

function ServiceCard({ svc, onView, onBook, imageUrl }) {
  const [hover, setHover] = useState(false);

  return (
    <div
      style={{
        ...S.card,
        borderColor: hover ? svc.color : "rgba(255,255,255,.08)",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div
        style={{
          ...S.imageWrap,
          borderColor: svc.color,
        }}
      >
        <img
          src={imageUrl(svc.image)}
          alt={svc.title}
          style={S.image}
          onError={(e) => (e.target.style.display = "none")}
        />
      </div>

      <h3 style={S.cardTitle}>{svc.title}</h3>

      <p style={S.cardDesc}>{svc.desc}</p>

      <div style={S.price}>💰 {svc.price}</div>

      <div style={S.actions}>
        <button style={S.viewBtn} onClick={onView}>
          👁 View
        </button>

        {/* <button style={S.bookBtnSmall} onClick={onBook}>
          📅 Book
        </button> */}
      </div>
    </div>
  );
}

const S = {
  section: {
    background: "#0A1628",
    padding: "90px 20px",
  },

  container: {
    maxWidth: 1200,
    margin: "0 auto",
  },

  heading: {
    fontSize: 42,
    fontWeight: 800,
    color: "#fff",
    marginBottom: 8,
  },

  sub: {
    color: "#9ca3af",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
    gap: 20,
  },

  card: {
    background: "#0f1c31",
    border: "1px solid rgba(255,255,255,.08)",
    borderRadius: 18,
    padding: 20,
    transition: ".2s",
  },

  imageWrap: {
    width: 60,
    height: 60,
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid",
    marginBottom: 14,
    background: "#13233d",
  },

  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  cardTitle: {
    color: "#fff",
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 10,
  },

  cardDesc: {
    color: "#b6c0cf",
    fontSize: 14,
    lineHeight: 1.6,
    minHeight: 72,
  },

  price: {
    color: "#fff",
    fontWeight: 700,
    margin: "14px 0",
  },

  actions: {
    display: "flex",
    gap: 10,
  },

  viewBtn: {
    flex: 1,
    padding: 10,
    borderRadius: 8,
    border: "1px solid #334155",
    background: "transparent",
    color: "#fff",
    cursor: "pointer",
  },

  // bookBtnSmall: {
  //   flex: 1,
  //   padding: 10,
  //   borderRadius: 8,
  //   border: "none",
  //   background: "#1D9E75",
  //   color: "#fff",
  //   cursor: "pointer",
  // },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
    padding: 20,
  },

  modal: {
    background: "#0f1c31",
    padding: 30,
    borderRadius: 18,
    width: "100%",
    maxWidth: 520,
    position: "relative",
  },

  closeBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    background: "transparent",
    border: "none",
    color: "#fff",
    fontSize: 20,
    cursor: "pointer",
  },

  modalDesc: {
    color: "#cbd5e1",
    lineHeight: 1.8,
    marginBottom: 20,
  },

  priceBox: {
    background: "#13233d",
    padding: 14,
    borderRadius: 10,
    color: "#fff",
    marginBottom: 20,
  },

  bookBtn: {
    width: "100%",
    padding: 14,
    borderRadius: 10,
    border: "none",
    background: "#1D9E75",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  },
};
